'use client'

import type { MarketplaceProduct, ProductSegment } from '@/types/marketplace'
import { SEGMENT_CONFIG_MAP } from '@/constants/microsoftCategories'
import { getProductSegmentImageUrl, getSegmentDisplayName, getHeadlineSegment } from '@/utils/microsoftFormatters'

interface ProductSchemaProps {
  product: MarketplaceProduct
  url: string
  /** When set, renders a single-Offer schema for this specific segment */
  segmentFilter?: ProductSegment
}

/**
 * Product JSON-LD structured data for Google Rich Results.
 *
 * Content is entirely from our own structured product data (not user input).
 * This follows the same established pattern as BreadcrumbSchema.tsx and
 * ArticleSchema.tsx in the codebase — JSON.stringify of trusted data objects.
 *
 * When `segmentFilter` is provided, the schema emits a single Offer with the
 * segment-specific SKU and price — this is used on segment sub-pages to give
 * Google a clean, unambiguous product+price signal for that specific segment.
 */
export default function ProductSchema({ product, url, segmentFilter }: ProductSchemaProps) {
  const segmentsToSchema = segmentFilter ? [segmentFilter] : product.segments

  const offers = segmentsToSchema
    .filter((seg) => seg.erpMonthly !== null || seg.erpAnnual !== null)
    .map((seg) => {
      const config = SEGMENT_CONFIG_MAP[seg.segment]
      // Canonical form matches getSegmentUrl in microsoftFormatters: segment
      // slugs are top-level routes under /marketplace/microsoft/, not nested
      // under the product slug. The nested form 404s.
      const segmentUrl = seg.segmentSlug
        ? `https://secureframe.com/marketplace/microsoft/${seg.segmentSlug}`
        : `https://secureframe.com${url}`
      const price = seg.erpMonthly ?? seg.erpAnnual ?? 0
      return {
        '@type': 'Offer',
        name: clampMerchantName(`${product.displayName} — ${config?.label ?? seg.segment}`),
        url: segmentUrl,
        price,
        priceCurrency: 'USD',
        // Google prices from Offer.price and ignores this spec, which only marks it as per seat-month.
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price,
          priceCurrency: 'USD',
          unitCode: seg.erpMonthly !== null ? 'MON' : 'ANN',
          // Secureframe sells annual terms only, whatever plans the Microsoft distributor lists
          billingDuration: { '@type': 'QuantitativeValue', value: 1, unitCode: 'ANN' },
        },
        priceValidUntil: getNextQuarterEnd(),
        availability: 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition',
        hasMerchantReturnPolicy: {
          '@type': 'MerchantReturnPolicy',
          applicableCountry: 'US',
          returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted',
        },
        shippingDetails: {
          '@type': 'OfferShippingDetails',
          shippingRate: { '@type': 'MonetaryAmount', value: '0.00', currency: 'USD' },
          shippingDestination: { '@type': 'DefinedRegion', addressCountry: 'US' },
          deliveryTime: {
            '@type': 'ShippingDeliveryTime',
            handlingTime: { '@type': 'QuantitativeValue', minValue: 0, maxValue: 0, unitCode: 'DAY' },
            transitTime: { '@type': 'QuantitativeValue', minValue: 0, maxValue: 0, unitCode: 'DAY' },
          },
        },
        seller: {
          '@type': 'Organization',
          name: 'Secureframe',
          url: 'https://secureframe.com',
        },
        ...(seg.msPartNumber && { sku: seg.msPartNumber, mpn: seg.msPartNumber }),
        description: `${config?.label ?? seg.segment} pricing — Annual billing`,
      }
    })

  // Google requires one of offers/review/aggregateRating on Product schema.
  // When a product has no priced segments (e.g., contact-for-pricing SKUs like
  // Azure pay-go), skip schema emission rather than ship an invalid empty offers.
  if (offers.length === 0) return null

  const schemaName = segmentFilter
    ? `${getSegmentDisplayName(product, segmentFilter.segment)} — ${SEGMENT_CONFIG_MAP[segmentFilter.segment]?.label ?? segmentFilter.segment}`
    : product.displayName

  // Determine billing term for schema name (Google Shopping requires "subscription" in name)
  const billingTerm = segmentFilter ? '(Annual Subscription)' : '(Subscription)'

  // Per-product merchant image (1500x1500 JPEG) — specific to the segment when
  // one is selected, otherwise the canonical headline segment's image so the
  // schema picture matches the headline price (commercial when present).
  const imageSegment = segmentFilter?.segment ?? getHeadlineSegment(product)?.segment ?? 'commercial'
  const productImage = getProductSegmentImageUrl(product, imageSegment)

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: clampMerchantName(`${schemaName} ${billingTerm}`),
    ...(product.description && { description: product.description }),
    image: productImage,
    brand: {
      '@type': 'Brand',
      name: product.vendorName ?? 'Microsoft',
    },
    category: 'Software Licenses',
    url: `https://secureframe.com${url}`,
    offers: offers.length === 1 ? offers[0] : offers,
  }

  // JSON.stringify of our own structured data — no user input, safe from XSS
  const jsonLd = JSON.stringify(schema)
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }} />
}

/**
 * ItemList schema for catalog/category pages.
 * Uses dangerouslySetInnerHTML following the same pattern as BreadcrumbSchema.tsx.
 * Content is from our own structured data, not user input.
 */
export function ItemListSchema({
  items,
  name,
  url,
}: {
  items: { name: string; url: string; position: number }[]
  name: string
  url: string
}) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name,
    url: `https://secureframe.com${url}`,
    numberOfItems: items.length,
    itemListElement: items.map((item) => ({
      '@type': 'ListItem',
      position: item.position,
      name: item.name,
      url: `https://secureframe.com${item.url}`,
    })),
  }

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
}

/**
 * Google Merchant listings reject Product/Offer `name` values longer than
 * 150 characters ("Invalid string length in field name" in Search Console).
 * Truncate at a word boundary; the billing-term suffix is what usually
 * pushes long Microsoft SKU names over the limit.
 */
function clampMerchantName(name: string, max = 150): string {
  if (name.length <= max) return name
  const cut = name.slice(0, max)
  const lastSpace = cut.lastIndexOf(' ')
  return (lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()
}

function getNextQuarterEnd(): string {
  const now = new Date()
  const quarter = Math.ceil((now.getMonth() + 1) / 3)
  const year = quarter === 4 ? now.getFullYear() + 1 : now.getFullYear()
  const month = (quarter % 4) * 3 + 3
  return `${year}-${String(month).padStart(2, '0')}-${month === 3 || month === 12 ? '31' : '30'}`
}
